/**
 * Search bar component.
 *
 * Reusable search input component with debounced search functionality.
 * Delays search execution until user stops typing to reduce unnecessary
 * search operations.
 *
 * @module SearchBar
 */

import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface SearchBarProps {
  readonly placeholder: string;
  readonly onSearch: (query: string) => void;
  readonly debounceMs?: number;
  /** Merged into the input; default keeps `max-w-sm` for backward compatibility. */
  readonly className?: string;
}

export function SearchBar({ placeholder, onSearch, debounceMs = 300, className }: SearchBarProps) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(query);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [query, onSearch, debounceMs]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className={cn('pl-9 w-full max-w-sm', className)}
      />
    </div>
  );
}

